<template>
    <div class="export-exclusions" v-if="data && data.available">

        <div v-if="data.excluded === 0" class="export-exclusions__allclear">
            <span class="export-exclusions__allclear-icon"><i class="fal fa-check"></i></span>
            <span class="export-exclusions__allclear-text">
                <b>{{ trans('allclear_title').replace(':count', data.total) }}</b>
                <span>{{ trans('allclear_note') }}</span>
            </span>
        </div>

        <template v-else>
            <div class="export-exclusions__coverage">
                <div class="export-exclusions__coverage-head">
                    <span class="export-exclusions__count">
                        <b>{{ data.exportable }}</b> {{ trans('coverage_of').replace(':total', data.total) }}
                    </span>
                </div>

                <div class="export-exclusions__bar" role="img" :aria-label="coverageLabel">
                    <div class="export-exclusions__bar-seg is-ok" :style="{ width: exportablePercent + '%' }"></div>
                    <div class="export-exclusions__bar-seg is-out" :style="{ width: (100 - exportablePercent) + '%' }"></div>
                </div>

                <div class="export-exclusions__legend">
                    <span><i class="is-ok"></i>{{ trans('legend_ok') }}</span>
                    <span><i class="is-out"></i>{{ trans('legend_out') }}</span>
                </div>
            </div>

            <div class="export-exclusions__groups">

                <div
                    v-for="group in data.groups"
                    :key="group.reason"
                    class="export-exclusions__group"
                    :class="[
                        'is-' + group.severity,
                        isOpen(group) ? 'is-open' : 'is-collapsed',
                        group.count === 0 ? 'is-empty' : ''
                    ]"
                >
                    <button
                        type="button"
                        class="export-exclusions__group-head"
                        :aria-expanded="isOpen(group) ? 'true' : 'false'"
                        :disabled="group.count === 0"
                        @click="toggle(group)"
                    >
                        <span class="export-exclusions__group-icon"><i :class="'fal fa-fw ' + reasonIcon(group.reason)"></i></span>
                        <span class="export-exclusions__group-text">
                            <span class="export-exclusions__group-name">{{ trans('reason_' + group.reason) }}</span>
                            <span class="export-exclusions__group-desc">{{ trans('reason_' + group.reason + '_desc') }}</span>
                        </span>
                        <span class="export-exclusions__group-count">{{ group.count }}</span>
                        <span class="export-exclusions__chev"><i class="fal fa-fw fa-chevron-right"></i></span>
                    </button>

                    <div class="export-exclusions__group-body" v-if="isOpen(group) && group.count > 0">
                        <ul class="export-exclusions__people" :data-reason="group.reason" :style="columnStyle(group)">
                            <li v-for="employee in group.employees" :key="employee.employee_contract_id">
                                <span class="export-exclusions__cell is-wage" v-if="hasWageNumbers(group)">
                                    <span class="badge badge-light" v-if="employee.wage_personal_number">{{ employee.wage_personal_number }}</span>
                                </span>

                                <span class="export-exclusions__cell is-contract">
                                    <span class="badge badge-warning contract-type" v-if="employee.contract_type">{{ employee.contract_type }}</span>
                                </span>

                                <span class="export-exclusions__person-name">{{ employee.name }}</span>

                                <span v-if="employee.locked_days !== null" class="export-exclusions__tag">
                                    {{ employee.locked_days }} / {{ employee.days_in_month }} {{ trans('label_days') }}
                                </span>

                                <a v-if="employee.fix_url" class="btn btn-sm btn-success text-white export-exclusions__fix" :href="employee.fix_url" target="_blank">
                                    <i class="far mr-1" :class="fixIcon(group.reason)"></i> {{ trans('fix_' + group.reason) }}
                                </a>
                            </li>
                        </ul>

                        <div class="export-exclusions__group-foot" v-if="group.action_url">
                            <a class="ml-auto" :href="group.action_url" target="_blank">{{ trans('action_' + group.reason) }}</a>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>


<script>
    import TranslationsUtils from '../../app-components/TranslationsUtils';

    export default {
        name: 'export-exclusions-panel',

        mixins: [TranslationsUtils],

        props: {
            'url': {
                type: String
            },
            'year': {
                type: [String, Number]
            },
            'month': {
                type: [String, Number]
            },
            'restaurantId': {
                type: [String, Number]
            }
        },

        data() {
            return {
                loading: false,
                data: null,
                openState: {},
                columnWidths: {}
            }
        },

        computed: {
            exportablePercent() {
                if (!this.data || !this.data.total) {
                    return 0;
                }

                return Math.round((this.data.exportable / this.data.total) * 100);
            },

            coverageLabel() {
                if (!this.data) {
                    return '';
                }

                return this.data.exportable + ' ' + this.trans('coverage_of').replace(':total', this.data.total);
            }
        },

        methods: {
            load() {
                this.data = null;
                this.openState = {};
                this.loading = true;

                axios.post(this.buildUrl(), null)
                    .then((response) => {
                        this.data = response.data;
                        this.loading = false;

                        this.$emit('loaded', this.data);
                    }, () => {
                        this.loading = false;
                        this.data = null;
                        this.$emit('loaded', null);
                    });
            },

            buildUrl() {
                return this.url
                    .replace('YEAR', this.year)
                    .replace('MONTH', this.month)
                    .replace('RESTAURANT', this.restaurantId);
            },

            isOpen(group) {
                return !!this.openState[group.reason];
            },

            toggle(group) {
                if (group.count === 0) {
                    return;
                }

                this.$set(this.openState, group.reason, !this.openState[group.reason]);

                if (this.isOpen(group)) {
                    this.$nextTick(() => this.measureColumns(group));
                }
            },

            hasWageNumbers(group) {
                return group.employees.some((employee) => !!employee.wage_personal_number);
            },

            // badge columns are as wide as the widest badge in the group, so the
            // names line up without ever cutting an abbreviation off
            measureColumns(group) {
                const list = this.$el.querySelector('.export-exclusions__people[data-reason="' + group.reason + '"]');

                if (!list) {
                    return;
                }

                this.$set(this.columnWidths, group.reason, {
                    wage: this.widestBadge(list, '.is-wage .badge'),
                    contract: this.widestBadge(list, '.is-contract .badge')
                });
            },

            widestBadge(list, selector) {
                return Array.prototype.reduce.call(
                    list.querySelectorAll(selector),
                    (widest, badge) => Math.max(widest, Math.ceil(badge.getBoundingClientRect().width)),
                    0
                );
            },

            columnStyle(group) {
                const widths = this.columnWidths[group.reason];

                if (!widths) {
                    return {};
                }

                return {
                    '--ee-wage-col': widths.wage + 'px',
                    '--ee-contract-col': widths.contract + 'px'
                };
            },

            fixIcon(reason) {
                switch (reason) {
                    case 'not_locked':
                        return 'fa-lock';
                    case 'no_wage_number':
                        return 'fa-id-card';
                    case 'contract_type_not_mapped':
                        return 'fa-sliders-h';
                    default:
                        return 'fa-pen';
                }
            },

            reasonIcon(reason) {
                switch (reason) {
                    case 'not_locked':
                        return 'fa-lock-open';
                    case 'no_wage_number':
                        return 'fa-id-card';
                    case 'contract_type_not_mapped':
                        return 'fa-exclamation-triangle';
                    default:
                        return 'fa-minus-circle';
                }
            }
        }
    };
</script>



// WEBPACK FOOTER //
// resources/assets/admin/js/attendance/modals/ExportExclusionsPanel.vue